import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 用於偵測全局條碼掃描的 React Hook
 * @param {object} options
 * @param {React.RefObject} options.targetRef - 條碼輸入目標元素的 ref
 * @param {number} [options.threshold=30] - 判斷為掃描的最大按鍵間隔時間 (ms)
 * @param {string} [options.terminatorKey='Enter'] - 掃描結束的按鍵
 * @param {boolean} [options.enabled=true] - 是否啟用掃描功能
 */
const useGlobalBarcodeScanner = ({ targetRef, threshold = 30, terminatorKey = "Enter", enabled = true } = { targetRef: null }) => {
	// 儲存最後掃描到的條碼
	const [lastScannedCode, setLastScannedCode] = useState(null);

	// 重置最後掃描到的條碼
	const resetScannedCode = useCallback(
		() => {
			setLastScannedCode(null);
		},
		// eslint-disable-next-line
		[],
	);

	// 儲存正在輸入的字元緩衝區
	const keyBufferRef = useRef([]);

	// 儲存上次按鍵的時間戳
	const lastKeyTimeRef = useRef(0);

	// 核心的 handleGlobalKeyDown 事件處理器
	const handleGlobalKeyDown = useCallback(
		e => {
			// 如果未啟用掃描功能，則不處理
			if (!enabled) {
				keyBufferRef.current = [];
				return;
			}

			// 如果沒有指定目標元素，則不處理
			if (!targetRef || !targetRef.current) {
				keyBufferRef.current = [];
				return;
			}

			// 獲取實際的 DOM 元素（TextField 組件使用 useImperativeHandle，需要調用 node() 方法）
			const getTargetElement = () => {
				if (typeof targetRef.current?.node === "function") {
					// TextField 組件的情況
					return targetRef.current.node();
				}
				// 直接是 DOM 元素的情況
				return targetRef.current;
			};
			const targetElement = getTargetElement();
			if (!targetElement) {
				keyBufferRef.current = [];
				return;
			}
			// 檢查焦點是否在其他 input
			const activeEl = document.activeElement;
			if (activeEl && (activeEl.tagName === "TEXTAREA" || (activeEl.tagName === "INPUT" && activeEl !== targetElement))) {
				keyBufferRef.current = [];
				return;
			}

			// 如果焦點在目標輸入框上，且輸入框有值（可能是手動輸入），清空緩衝區
			// 這樣可以避免貼上操作後，緩衝區只留下最後一個字符（如 "v"）的問題
			// if (activeEl === targetElement && targetElement.value && targetElement.value.length > 1) {
			// 	keyBufferRef.current = [];
			// }

			const { key } = e;
			const currentTime = Date.now();
			const timeDiff = currentTime - lastKeyTimeRef.current;

			// 更新最後按鍵時間
			lastKeyTimeRef.current = currentTime;

			// --- 核心邏輯開始 ---
			// 1. 檢查是否為結束鍵 (例如 'Enter')
			if (key === terminatorKey) {
				// 如果緩衝區有內容，則視為完成一次掃描
				if (keyBufferRef.current.length > 0) {
					// 阻止預設行為（例如表單提交）
					e.preventDefault();
					// 阻止事件冒泡，避免觸發其他全局事件處理器
					e.stopPropagation();

					const scannedCode = keyBufferRef.current.join("");
					setLastScannedCode(scannedCode);

					// 獲取實際的 DOM 元素並設置焦點
					const targetElement = getTargetElement();
					if (targetElement && typeof targetElement.focus === "function") {
						targetElement.focus();
					}
					if (targetElement && typeof targetElement.select === "function") {
						targetElement.select();
					}
				}
				// 清空緩衝區
				keyBufferRef.current = [];
				return;
			}

			// 2. 檢查時間差 (判斷是否為手動輸入)
			// 如果按鍵間隔太長，就重設緩衝區，因為這代表是手動輸入
			if (timeDiff > threshold) {
				keyBufferRef.current = [];
			}

			// 3. 將按鍵存入緩衝區
			// 我們只儲存單一字元 (過濾掉 'Shift', 'Control' 等功能鍵)
			// 排除 Ctrl/Cmd + 鍵（複製、貼上、剪下等），避免誤判為掃描輸入
			if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				keyBufferRef.current.push(key);
			}

			// --- 核心邏輯結束 ---
		},
		// eslint-disable-next-line
		[targetRef, threshold, terminatorKey, enabled],
	);

	useEffect(
		() => {
			document.addEventListener("keydown", handleGlobalKeyDown);
			return () => {
				document.removeEventListener("keydown", handleGlobalKeyDown);
			};
		},
		// eslint-disable-next-line
		[handleGlobalKeyDown],
	);

	return { lastScannedCode, resetScannedCode };
};

export default useGlobalBarcodeScanner;
